import { useEffect, useState } from 'react'
import { Alert, Badge, Button } from '../ui/primitives'

function domainStatus(row) {
  if (!row) return { color: 'secondary', label: 'Not created' }
  if (row.status === 'active') return { color: 'success', label: 'Ready' }
  if (row.error_message) return { color: 'warning', label: 'Needs attention' }
  return { color: 'warning', label: 'Waiting for DNS' }
}

export default function VanitySetupStep({ step, onBack, onNext, onCreate, onDelete, onRefresh }) {
  const hostnames = step?.hostnames || []
  const inferredBase = hostnames[0]?.hostname?.split('.').slice(1).join('.') || ''
  const [base, setBase] = useState(inferredBase)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const dashboard = hostnames.find((row) => row.kind === 'dashboard')
  const api = hostnames.find((row) => row.kind === 'api')
  const complete = step?.status === 'complete'

  useEffect(() => {
    if (!base && inferredBase) setBase(inferredBase)
  }, [base, inferredBase])

  const create = async () => {
    const normalized = base.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '')
    if (!/^([a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i.test(normalized)) {
      setError('Enter a valid base domain, for example yourcompany.com')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onCreate(normalized)
      setBase(normalized)
    } catch {
      setError('We couldn\'t create the domains. Check the domain and try again.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (row) => {
    if (!window.confirm(`Disconnect "${row.hostname}" from Operations?`)) return
    setSaving(true)
    setError(null)
    try {
      await onDelete(row.id)
    } catch {
      setError('We couldn\'t disconnect this domain. Try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-[var(--app-text)]">Connect your domains</h2>
          <p className="mt-1 text-sm text-[var(--app-text-muted)]">
            Enter one base domain. Your dashboard and API each need a CNAME before setup can continue.
          </p>
        </div>
        {complete && <Badge color="success">Complete</Badge>}
      </div>

      {(error || step?.error) && <Alert color="danger">{error || 'Domain status could not be refreshed. Try again.'}</Alert>}

      {hostnames.length < 2 && (
        <div className="block space-y-2 text-sm font-medium text-[var(--app-text)]">
          <label htmlFor="setup-vanity-base">Base domain</label>
          <input id="setup-vanity-base" type="text" value={base} disabled={hostnames.length > 0} onChange={(event) => setBase(event.target.value)} placeholder="yourcompany.com…" spellCheck={false} className="w-full rounded-[var(--app-radius)] border border-[var(--app-border-strong)] bg-[var(--app-surface)] px-2.5 py-1.5 text-sm text-[var(--app-text)] disabled:opacity-55" />
          <span className="block text-xs font-normal text-[var(--app-text-muted)]">
            dashboard.{base || 'yourcompany.com'} and api.{base || 'yourcompany.com'}
          </span>
          {hostnames.length > 0 && <span className="block text-xs font-normal text-[var(--app-text-muted)]">Delete the existing hostname to choose a different base domain.</span>}
        </div>
      )}

      <div className="space-y-2">
        {['dashboard', 'api'].map((kind) => {
          const row = kind === 'dashboard' ? dashboard : api
          const status = domainStatus(row)
          return (
            <div key={kind} className="rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-surface-2)] p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-[var(--app-text-muted)]">{kind}</div>
                  <div className="mt-1 font-medium text-[var(--app-text)]">{row?.hostname || 'Not created'}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge color={status.color}>{status.label}</Badge>
                  {row && <Button size="sm" color="danger" disabled={saving} onClick={() => remove(row)}>Delete</Button>}
                </div>
              </div>
              {row?.cname_target && row.status !== 'active' && (
                <div className="mt-3 text-xs text-[var(--app-text-muted)]">
                  Create CNAME <code className="text-[var(--app-text)]">{row.hostname}</code> → <code className="text-[var(--app-text)]">{row.cname_target}</code>
                </div>
              )}
              {row?.error_message && <div className="mt-2 text-xs text-[var(--app-crit)]">Check the CNAME, then refresh the status.</div>}
            </div>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button onClick={onBack}>Back</Button>
        <div className="flex gap-2">
          {!complete && hostnames.length < 2 && <Button color="primary" disabled={saving} onClick={create}>{saving ? 'Applying…' : 'Create domains'}</Button>}
          {!complete && hostnames.length > 0 && <Button disabled={saving} onClick={onRefresh}>Refresh status</Button>}
          {complete && <Button color="primary" onClick={onNext}>Next</Button>}
        </div>
      </div>
    </div>
  )
}
